import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'
import { Trans, useTranslation } from 'react-i18next'

import { Box, ButtonGroup, Text, Tooltip } from '@chakra-ui/react'

import { ToggleButton } from '@/components/ui'
import { IncidentsView } from '@/redux/incidents/incidentsSlice'
import { BUTTON_PRESS, trackEvent } from '@/utils/analytics'

interface IProps {
  view: IncidentsView
  onSelectView: (view: IncidentsView) => void
}

interface PriorityQueueOptionIProps {
  isActive: boolean
  onClick: () => void
}

// Extracted so TS doesn't have to fold this Tooltip-wrapped button's JSX into
// the same expression as ButtonGroup's other children — inlined, that mix
// blows up into a "union type too complex to represent" (TS2590).
const PriorityQueueOption = ({
  isActive,
  onClick,
}: PriorityQueueOptionIProps) => {
  const { t } = useTranslation()

  return (
    <Tooltip
      hasArrow
      label={
        <Box>
          <Text fontWeight='bold'>{t('incidents:priority_queue_toggle')}</Text>
          <Text>
            <Trans
              components={{ b: <Text as='span' fontWeight='bold' /> }}
              i18nKey={'incidents:priority_queue_tooltip_description' as never}
            />
          </Text>
        </Box>
      }
      openDelay={500}
      placement='bottom'
    >
      <ToggleButton
        ariaLabel='toggle priority queue'
        bgColor={isActive ? 'btn.100' : 'white'}
        borderColor={isActive ? 'btn.100' : 'gray.200'}
        dataTestId='incidentsPage_tableHeader_priorityQueueToggle'
        handleOnClick={onClick}
        isDisabled={isActive}
        textColor={isActive ? 'white' : 'gray.800'}
      >
        {t('incidents:priority_queue_toggle')}
      </ToggleButton>
    </Tooltip>
  )
}

/**
 * Priority Queue, All, and Acknowledged by Me as mutually exclusive options, in
 * that left-to-right order (ENG-32284). Supersedes IncidentsOwnerToggle, which
 * rendered Priority Queue as an additive toggle stacked on top of the owner
 * filter — selecting one here always deselects the other two.
 */
export const IncidentsViewToggle = ({ view, onSelectView }: IProps) => {
  const { t } = useTranslation()
  const isPriorityQueueEnabled = useFeatureFlag(FeatureFlag.priorityQueue)

  const isPriorityQueueActive = view === 'priorityQueue'
  const isAllActive = view === 'all'
  const isAcknowledgedByMeActive = view === 'acknowledgedByMe'

  const onSelectAll = () => {
    trackEvent(`${BUTTON_PRESS} Filter All Incidents`)
    onSelectView('all')
  }

  const onSelectAcknowledgedByMe = () => {
    trackEvent(`${BUTTON_PRESS} Filter Incidents Acknowledged by Me`)
    onSelectView('acknowledgedByMe')
  }

  return (
    <ButtonGroup h='40px' isAttached size='sm' variant='outline'>
      {isPriorityQueueEnabled && (
        <PriorityQueueOption
          isActive={isPriorityQueueActive}
          onClick={() => onSelectView('priorityQueue')}
        />
      )}
      <ToggleButton
        ariaLabel={t('incidents:filter_owner_all_aria')}
        bgColor={isAllActive ? 'btn.100' : 'white'}
        borderColor={isAllActive ? 'btn.100' : 'gray.200'}
        dataTestId='incidentsPage_tableHeader_allButton'
        handleOnClick={onSelectAll}
        isDisabled={isAllActive}
        textColor={isAllActive ? 'white' : 'gray.800'}
      >
        {t('incidents:filter_owner_all')}
      </ToggleButton>
      <ToggleButton
        ariaLabel={t('incidents:filter_owner_me_aria')}
        bgColor={isAcknowledgedByMeActive ? 'btn.100' : 'white'}
        borderColor={isAcknowledgedByMeActive ? 'btn.100' : 'gray.200'}
        dataTestId='incidentsPage_tableHeader_acknowledgedByMeButton'
        handleOnClick={onSelectAcknowledgedByMe}
        isDisabled={isAcknowledgedByMeActive}
        textColor={isAcknowledgedByMeActive ? 'white' : 'gray.800'}
      >
        {t('incidents:filter_owner_me')}
      </ToggleButton>
    </ButtonGroup>
  )
}
